import { ConnectProto } from "@racoon/api";

import { containerVolume, estimatedVolume } from "./container-volume";
import { plannedContainerCount } from "~/shared/lib/job-containers";

type ContainerNote = {
  coefficients: number[];
  numOfContainer?: number;
  factTotalVolume?: number;
};

// No fills reported: each container of the group counts as full (1.0), the job's
// own count standing in for jobs without a group. Only shown until the first edit
// writes them.
const defaultCoefficients = (job: ConnectProto.Job) => {
  const count = plannedContainerCount(job) || Math.round(job.numOfContainer ?? 0);
  return Array.from({ length: count }, () => 1);
};

const noteOf = (job: ConnectProto.Job): ContainerNote => ({
  coefficients:
    job.containerCoefficients.length > 0
      ? [...job.containerCoefficients]
      : defaultCoefficients(job),
  numOfContainer: job.numOfContainer,
  factTotalVolume: job.factTotalVolume,
});

/**
 * The job's container facts. Edits go to `onChange` only, as a draft: the report
 * body writes them together with the review status, so a report closed without
 * one leaves the job's facts as they were.
 */
export const useContainerNote = (
  job: ConnectProto.Job,
  onChange: (job: ConnectProto.Job) => void,
) => {
  const note = noteOf(job);
  const volume = containerVolume(job);

  // The backend derives num_of_container (one per coefficient) and fact_total_volume
  // from the coefficients. An unset volume reaches the store as undefined
  // (normalizeBigInt copies own keys only), though protobuf-es types it as number.
  const setCoefficients = (coefficients: number[]) =>
    onChange({
      ...job,
      containerCoefficients: coefficients,
      numOfContainer: coefficients.length,
      factTotalVolume: estimatedVolume(coefficients, coefficients.length, volume) as number,
    });

  const setCoefficient = (index: number, value: number) =>
    setCoefficients(note.coefficients.map((c, i) => (i === index ? value : c)));

  // A new container counts as full. The last one can't be removed: the backend
  // ignores an empty coefficient list.
  const addContainer = () => setCoefficients([...note.coefficients, 1]);
  const removeContainer = () => setCoefficients(note.coefficients.slice(0, -1));

  return {
    coefficients: note.coefficients,
    factVolume:
      note.factTotalVolume ?? estimatedVolume(note.coefficients, note.numOfContainer, volume),
    setCoefficient,
    addContainer,
    removeContainer,
  };
};
